import { XMarkIcon } from '@heroicons/react/24/outline';

import { forwardRef, useRef, useState } from 'react';

import { mergeRefs } from '@/utils/merge-refs';
import { cn } from '@/utils/tailwind';

export const MultipleArrayInput = forwardRef<
  HTMLInputElement,
  {
    id?: string;
    value: Array<string> | undefined;
    onChange: (value: Array<string>) => void;
    validate?: (value: string) => boolean;
    className?: string;
    maxLength?: number;
    separators?: string | RegExp;
    disabled?: boolean;
    placeholder?: string;
  }
>((props, ref) => {
  const separators = props.separators ?? ',';

  const validate = props.validate ?? ((x) => !!x);

  const [text, setText] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const pasteMarkRef = useRef<boolean>(false);

  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-2 rounded-md border border-secondary bg-background p-2 focus-within:outline focus-within:outline-2 focus-within:outline-brand-green-500',
        props.disabled && 'pointer-events-none opacity-50',
        props.placeholder && 'relative',
        props.className
      )}
      onClick={() => {
        inputRef.current?.focus();
      }}
    >
      {props.placeholder && !props.value?.length && !text && (
        <span className="pointer-events-none absolute inset-0 ml-0.5 flex items-center px-2 text-tertiary opacity-50">
          {props.placeholder}
        </span>
      )}
      {props.value?.map((email, idx) => (
        <span
          className="flex items-center gap-1 rounded-md bg-tertiary px-2"
          key={idx}
        >
          <span>{email}</span>
          <button
            type="button"
            className={cn(
              'flex-shrink-0',
              props.disabled && 'cursor-not-allowed'
            )}
            onClick={() => {
              props.onChange((props.value ?? []).filter((_, i) => i !== idx));
            }}
            disabled={props.disabled}
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        </span>
      ))}

      <div className="grid pl-0.5">
        <input
          id={props.id}
          disabled={props.disabled}
          value={text}
          ref={mergeRefs([inputRef, ref])}
          onBlur={() => {
            if (
              validate(text) &&
              (props.maxLength == null ||
                (props.value ?? []).length < props.maxLength)
            ) {
              props.onChange([...(props.value ?? []), text]);
              setText('');
            }
          }}
          onChange={(e) => {
            if (pasteMarkRef.current) {
              const newValues = e.target.value
                .split(separators)
                .map((i) => i.trim())
                .filter(validate);

              const permitted =
                props.maxLength != null
                  ? Math.max(0, props.maxLength - (props.value ?? []).length)
                  : newValues.length;

              props.onChange([
                ...(props.value ?? []),
                ...newValues.slice(0, permitted),
              ]);
              setText(newValues.slice(permitted).join(','));
            } else {
              setText(e.target.value);
            }

            pasteMarkRef.current = false;
          }}
          onPaste={() => {
            pasteMarkRef.current = true;
          }}
          onKeyDown={(e) => {
            if (
              e.key === 'Enter' ||
              e.key === 'Tab' ||
              e.key.match(separators) != null
            ) {
              e.preventDefault();
              if (
                validate(text) &&
                (props.maxLength == null ||
                  (props.value ?? []).length < props.maxLength)
              ) {
                props.onChange([...(props.value ?? []), text]);
                setText('');
              }
            } else if (e.key === 'Backspace') {
              if (
                e.currentTarget.selectionStart ===
                  e.currentTarget.selectionEnd &&
                e.currentTarget.selectionStart === 0
              ) {
                props.onChange((props.value ?? []).slice(0, -1));
              }
            }
          }}
          size={1}
          className={cn(
            'col-[1] row-[1] w-auto min-w-0 bg-transparent leading-none shadow-none outline-none',
            props.disabled && 'cursor-not-allowed'
          )}
        />
        <div className="pointer-events-none col-[1] row-[1] translate-y-[2px] whitespace-pre-wrap leading-none opacity-0">
          {text}
        </div>
      </div>
    </div>
  );
});
